'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { useAuthStore } from '@/stores/authStore'
import { useTranslation } from '@/lib/i18n/useTranslation'
import { Eye, EyeOff, LogIn, ShieldCheck, ArrowLeft } from 'lucide-react'
import Link from 'next/link'

export default function InstallerLoginPage() {
  const router = useRouter()
  const login = useAuthStore((s) => s.login)
  const confirmDevice = useAuthStore((s) => s.confirmDevice)
  const confirmTotp = useAuthStore((s) => s.confirmTotp)
  const { t } = useTranslation()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [activationCode, setActivationCode] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Krok potwierdzenia: nowe urządzenie (kod z e-maila) LUB 2FA admina (TOTP).
  const [step, setStep] = useState<'login' | 'confirm'>('login')
  const [confirmMode, setConfirmMode] = useState<'device' | 'totp'>('device')
  const [challengeId, setChallengeId] = useState('')
  const [emailHint, setEmailHint] = useState('')
  const [code, setCode] = useState('')
  const [remember, setRemember] = useState(true)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const res = await login(email, password)
      if (res.requiresDeviceConfirmation) {
        setConfirmMode('device')
        setChallengeId(res.challengeId || '')
        setEmailHint(res.emailHint || '')
        setStep('confirm')
      } else if (res.requiresTotp) {
        setConfirmMode('totp')
        setChallengeId(res.challengeId || '')
        setStep('confirm')
      } else if (res.ok) {
        router.push('/installer/dashboard')
      } else {
        setError(res.error || t('invalidCredentials'))
      }
    } catch {
      setError(t('loginError'))
    } finally {
      setLoading(false)
    }
  }

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const res = confirmMode === 'totp'
        ? await confirmTotp(challengeId, code.trim())
        : await confirmDevice(challengeId, code.trim(), remember)
      if (res.ok) {
        router.push('/installer/dashboard')
      } else {
        setError(res.error || t('inst.wrongCode'))
      }
    } catch {
      setError(t('loginError'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-installer-bg">
      {/* Background gradient */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-installer-bg via-installer-bg to-emerald-dark/20" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-heiz-orange/5 rounded-full blur-3xl" />
      </div>

      {/* Login card */}
      <div className="relative z-10 w-full max-w-md mx-4">
        {/* Logo area */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <img src="/logo-mark.svg" alt="HeizOS" style={{ height: 48, width: 'auto', display: 'inline-block' }} />
            <h1 className="text-2xl font-bold text-white">
              Heiz<span className="text-heiz-orange">OS</span>
            </h1>
          </div>
          <p className="text-gray-1 text-sm mt-2">{t('forInstallers')}</p>
        </div>

        {step === 'login' ? (
          /* ── Logowanie ── */
          <form
            onSubmit={handleSubmit}
            className="bg-installer-card border border-installer-border rounded-2xl p-8 shadow-2xl"
          >
            <h2 className="text-xl font-semibold text-white mb-6">{t('login')}</h2>

            {error && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* Email */}
            <div className="mb-4">
              <label className="block text-sm text-gray-1 mb-1.5">{t('email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@firma.de"
                required
                className="w-full px-4 py-3 rounded-xl bg-installer-surface border border-installer-border
                           text-white placeholder:text-gray-1/50 focus:border-heiz-orange focus:ring-1
                           focus:ring-heiz-orange/30 outline-none transition-all text-sm"
              />
            </div>

            {/* Password */}
            <div className="mb-4">
              <label className="block text-sm text-gray-1 mb-1.5">{t('password')}</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('yourPassword')}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-installer-surface border border-installer-border
                             text-white placeholder:text-gray-1/50 focus:border-emerald focus:ring-1
                             focus:ring-emerald/30 outline-none transition-all text-sm pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-1 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Activation Code */}
            <div className="mb-6">
              <label className="block text-sm text-gray-1 mb-1.5">
                {t('activationCode')} <span className="text-gray-1/50">({t('optional')})</span>
              </label>
              <input
                type="text"
                value={activationCode}
                onChange={(e) => setActivationCode(e.target.value)}
                placeholder="XXXX-XXXX"
                className="w-full px-4 py-3 rounded-xl bg-installer-surface border border-installer-border
                           text-white placeholder:text-gray-1/50 focus:border-heiz-orange focus:ring-1
                           focus:ring-heiz-orange/30 outline-none transition-all text-sm"
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl
                         bg-gradient-to-r from-heiz-orange to-[#c43a22] text-white font-semibold
                         hover:from-[#ff6b4a] hover:to-heiz-orange transition-all disabled:opacity-50
                         disabled:cursor-not-allowed shadow-[0_0_20px_rgba(233,76,50,0.3)] text-sm"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <LogIn className="w-4 h-4" />
                  {t('loginBtn')}
                </>
              )}
            </button>

            {/* Links */}
            <div className="mt-4 text-center">
              <Link href="/installer/reset" className="text-xs text-gray-1 hover:text-heiz-orange transition-colors">
                {t('forgotPassword')}
              </Link>
            </div>
          </form>
        ) : (
          /* ── Potwierdzenie nowego urządzenia ── */
          <form
            onSubmit={handleConfirm}
            className="bg-installer-card border border-installer-border rounded-2xl p-8 shadow-2xl"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="w-9 h-9 rounded-xl bg-emerald/15 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-emerald" />
              </span>
              <h2 className="text-xl font-semibold text-white">
                {confirmMode === 'totp' ? t('inst.twoFactorTitle') : t('inst.confirmNewDevice')}
              </h2>
            </div>
            <p className="text-sm text-gray-1 mb-6">
              {confirmMode === 'totp' ? (
                <>{t('inst.totpPrompt')}</>
              ) : (
                <>{t('inst.codeSentPrefix')} <span className="text-white">{emailHint}</span>{t('inst.codeSentSuffix')} {t('inst.deviceConfirmSecurity')}</>
              )}
            </p>

            {error && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm text-gray-1 mb-1.5">{t('inst.confirmationCode')}</label>
              <input
                type="text"
                inputMode="numeric"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                required
                className="w-full px-4 py-3 rounded-xl bg-installer-surface border border-installer-border
                           text-white text-center text-2xl tracking-[0.4em] font-mono placeholder:text-gray-1/40
                           focus:border-emerald focus:ring-1 focus:ring-emerald/30 outline-none transition-all"
              />
            </div>

            {confirmMode === 'device' && (
              <label className="flex items-center gap-2 mb-6 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="w-4 h-4 accent-emerald"
                />
                <span className="text-sm text-gray-1">{t('inst.trustDevice')}</span>
              </label>
            )}
            {confirmMode === 'totp' && <div className="mb-6" />}

            <button
              type="submit"
              disabled={loading || code.length < 6}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl
                         bg-gradient-to-r from-emerald to-emerald-dark text-white font-semibold
                         hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <ShieldCheck className="w-4 h-4" />
                  {t('inst.confirmAndLogin')}
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => { setStep('login'); setCode(''); setError('') }}
              className="mt-4 w-full flex items-center justify-center gap-1 text-xs text-gray-1 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-3 h-3" /> {t('inst.backToLogin')}
            </button>
          </form>
        )}

        {/* Register link */}
        <div className="mt-3 text-center">
          <Link
            href="/installer/register"
            className="text-xs text-gray-1 hover:text-heiz-orange transition-colors"
          >
            {t('inst.noAccountYet')} <span className="text-heiz-orange">{t('inst.registerAction')}</span>
          </Link>
        </div>

        <p className="text-center text-xs text-gray-1/50 mt-4">
          HeizOS v1.0
        </p>
      </div>
    </div>
  )
}
